/**
 * CareLogix™ ORACLE — Approval Expiry & Re-attestation™ (Phase 45, AdminOne).
 * Governance dashboard over Phase 44 approvals: expiry visibility, CEO
 * re-attestation, reminders, and auto-suspension status.
 */
import React, { useCallback, useEffect, useState } from "react";
import { approvalExpiryApi } from "@/api/deliveryApprovalClient";

const C = {
  panel: "rgba(13,26,15,0.85)", ivory: "#f5f5dc", ivoryD: "#d4d4aa",
  gold: "#facc15", emerald: "#7AC860", amber: "#fcd34d", red: "#ef4444",
  border: "rgba(245,200,66,0.30)",
};
const S = {
  wrap: { background: "#0a0f0a", color: C.ivory, padding: "24px 22px", fontFamily: "'Inter', system-ui, sans-serif", borderTop: `2px solid ${C.border}` },
  kicker: { color: C.gold, fontSize: 11, letterSpacing: "0.22em", fontWeight: 800, textTransform: "uppercase" },
  title: { fontFamily: "'Cinzel', serif", color: C.gold, fontSize: 24, margin: "6px 0 14px" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 16 },
  card: { background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 },
  num: { fontSize: 26, color: C.gold, fontWeight: 700 },
  lbl: { color: C.ivoryD, fontSize: 12 },
  tickerBar: { background: "rgba(245,200,66,0.08)", border: `1px solid ${C.border}`, borderRadius: 10, padding: "8px 12px", marginBottom: 14, fontSize: 13, color: C.amber },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", color: C.gold, borderBottom: `1px solid ${C.border}`, padding: "8px 10px", fontFamily: "'Space Mono', monospace", fontSize: 11 },
  td: { borderBottom: "1px solid rgba(245,200,66,0.12)", padding: "10px", verticalAlign: "top", color: C.ivoryD },
  btn: { background: C.gold, color: "#0a0a0a", border: "none", padding: "7px 12px", borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 12, minHeight: 40, marginRight: 6, marginTop: 4 },
  btnGhost: { background: "transparent", color: C.ivory, border: `1px solid ${C.border}` },
  badge: (fg) => ({ display: "inline-block", padding: "3px 9px", borderRadius: 999, fontSize: 11, fontWeight: 700, color: fg, border: `1px solid ${fg}` }),
  modalWrap: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 16 },
  modal: { background: "#0d1a0f", border: `1px solid ${C.gold}`, borderRadius: 16, padding: 24, maxWidth: 620, width: "100%", maxHeight: "88vh", overflowY: "auto" },
  input: { width: "100%", padding: "10px 12px", borderRadius: 8, background: "rgba(0,0,0,0.4)", color: C.ivory, border: `1px solid ${C.border}`, fontSize: 14, marginTop: 6 },
  err: { color: C.red, fontSize: 12.5, marginTop: 10 },
  note: { color: C.ivoryD, fontSize: 11.5, marginTop: 4, lineHeight: 1.5 },
};

const STATE_COLOR = {
  PRODUCTION_APPROVED: C.emerald, PRODUCTION_REATTESTATION_DUE: C.amber,
  PRODUCTION_SUSPENDED: C.red, PRODUCTION_REVOKED: C.red,
  INTERNAL_BETA: C.ivoryD, APPROVAL_PENDING: C.amber,
};

const CHECKLIST = [
  ["human_review_active", "Human review workflow remains active"],
  ["evidenceos_logging_active", "EvidenceOS logging remains active"],
  ["compliance_controls_current", "Compliance controls remain current"],
  ["pricing_current", "Service pricing remains current"],
  ["service_description_current", "Service descriptions remain accurate"],
  ["privacy_controls_current", "Privacy & client-data controls remain active"],
  ["truthfulness_labels_active", "Truthfulness labels remain enabled"],
  ["no_unresolved_critical_risks", "No unresolved CRITICAL risks exist"],
  ["regulated_guardrails_active", "Regulated-domain guardrails remain active"],
  ["external_delivery_config_appropriate", "External delivery configuration remains appropriate"],
];

function errText(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Request failed";
  if (typeof d === "string") return d;
  if (d.missing_attestations) return `${d.reason} — Missing: ${d.missing_attestations.join(", ")}`;
  if (d.unmet_requirements) return `${d.reason} — Unmet: ${d.unmet_requirements.join(", ")}`;
  return d.reason || JSON.stringify(d);
}

export const ApprovalExpiryReattestationPanel = () => {
  const [ov, setOv] = useState(null);
  const [err, setErr] = useState(null);
  const [modal, setModal] = useState(null);
  const [notes, setNotes] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [totp, setTotp] = useState("");
  const [checks, setChecks] = useState({});
  const [lifetime, setLifetime] = useState(90);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setErr(null);
    try { const r = await approvalExpiryApi.overview(); setOv(r.data); }
    catch (e) { setErr(errText(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openReattest = (svc) => {
    setModal({ svc }); setNotes(""); setConfirm(false); setTotp(""); setLifetime(90);
    setChecks(Object.fromEntries(CHECKLIST.map(([k]) => [k, false]))); setErr(null);
  };
  const closeModal = () => { setModal(null); setErr(null); };

  const runScan = async () => { setBusy(true); try { await approvalExpiryApi.scan(); await load(); } catch (e) { setErr(errText(e)); } finally { setBusy(false); } };

  const submitReattest = async () => {
    if (!modal) return;
    setBusy(true); setErr(null);
    try {
      await approvalExpiryApi.reattest(modal.svc.service_id, {
        supporting_notes: notes, confirm, totp_code: totp || undefined,
        attestations: checks, lifetime_days: lifetime, decision: "REAPPROVED",
      });
      await load(); closeModal();
    } catch (e) { setErr(errText(e)); }
    finally { setBusy(false); }
  };

  const allChecked = CHECKLIST.every(([k]) => checks[k]);
  const cards = ov?.cards || {};
  const CARD = [
    ["active_production_approvals", "Active Approvals"],
    ["expiring_30", "Expiring ≤30d"],
    ["expiring_14", "Expiring ≤14d"],
    ["reattestation_due", "Re-attestation Due"],
    ["expired", "Expired"],
    ["suspended_expiry", "Suspended (expiry)"],
    ["reattested_30d", "Re-attested 30d"],
    ["critical_risk_blocks", "Critical Risk Blocks"],
  ];

  return (
    <section style={S.wrap} data-testid="approval-expiry-reattestation-panel">
      <div style={S.kicker}>ORACLE · APPROVAL GOVERNANCE</div>
      <h2 style={S.title}>Approval Expiry &amp; Re-attestation™</h2>

      {(ov?.ticker_segments || []).map((seg) => (
        <div key={seg.id} style={S.tickerBar} data-testid="aer-ticker-segment">🔐 {seg.label}: {seg.value}</div>
      ))}

      <div style={S.grid} data-testid="aer-status-cards">
        {CARD.map(([k, label]) => (
          <div key={k} style={S.card}>
            <div style={S.lbl}>{label}</div>
            <div style={S.num} data-testid={`aer-card-${k}`}>{cards[k] ?? 0}</div>
          </div>
        ))}
      </div>

      <button style={{ ...S.btn, ...S.btnGhost, marginBottom: 12 }} onClick={runScan} disabled={busy} data-testid="aer-run-scan" type="button">
        {busy ? "Scanning…" : "↻ Run Expiry Scan"}
      </button>
      {err && !modal && <p style={S.err} data-testid="aer-error">{err}</p>}

      <div style={{ overflowX: "auto" }}>
        <table style={S.table} data-testid="aer-table">
          <thead>
            <tr>
              <th style={S.th}>Service</th><th style={S.th}>Category</th><th style={S.th}>State</th>
              <th style={S.th}>Expiry Date</th><th style={S.th}>Days Remaining</th>
              <th style={S.th}>Risk</th><th style={S.th}>Last Re-attested</th><th style={S.th}>Action</th>
            </tr>
          </thead>
          <tbody>
            {(ov?.services || []).map((s) => (
              <tr key={s.service_id} data-testid={`aer-row-${s.service_id}`}>
                <td style={{ ...S.td, color: C.ivory }}>{s.service_name}</td>
                <td style={S.td}>{s.category}</td>
                <td style={S.td}><span style={S.badge(STATE_COLOR[s.delivery_state] || C.ivoryD)} data-testid={`aer-state-${s.service_id}`}>{s.delivery_state}</span></td>
                <td style={S.td}>{s.expires_at ? s.expires_at.slice(0, 10) : "—"}</td>
                <td style={S.td}>{s.days_remaining === null || s.days_remaining === undefined ? "—" : `${s.days_remaining} day(s)`}</td>
                <td style={S.td}>{s.compliance_risk}</td>
                <td style={S.td}>{s.last_reattested_at ? s.last_reattested_at.slice(0, 10) : "—"}</td>
                <td style={S.td}>
                  {(s.delivery_state === "PRODUCTION_APPROVED" || s.delivery_state === "PRODUCTION_REATTESTATION_DUE") && (
                    <button style={S.btn} onClick={() => openReattest(s)} data-testid={`aer-reattest-${s.service_id}`} type="button">Re-attest Now</button>
                  )}
                  {s.delivery_state === "PRODUCTION_SUSPENDED" && (
                    <button style={{ ...S.btn, ...S.btnGhost }} onClick={() => openReattest(s)} data-testid={`aer-reactivate-${s.service_id}`} type="button">Reactivate via Re-attest</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div style={S.modalWrap} role="dialog" aria-modal="true" aria-label="Re-attestation" data-testid="aer-reattest-modal">
          <div style={S.modal}>
            <div style={S.kicker}>RE-ATTEST</div>
            <h3 style={{ ...S.title, fontSize: 19, margin: "6px 0 4px" }}>{modal.svc.service_name}</h3>
            <p style={S.note}>Current state: {modal.svc.delivery_state} · Expiry: {modal.svc.expires_at ? modal.svc.expires_at.slice(0, 10) : "—"} · Risk: {modal.svc.compliance_risk}</p>
            <p style={S.note}>Every re-attestation writes an immutable EvidenceOS record. Human review remains mandatory.</p>

            <fieldset style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, marginTop: 12 }}>
              <legend style={{ ...S.lbl, padding: "0 6px" }}>Attestation checklist (all required)</legend>
              {CHECKLIST.map(([k, label]) => (
                <label key={k} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 8, fontSize: 12.5, color: C.ivoryD }}>
                  <input type="checkbox" checked={!!checks[k]} onChange={(e) => setChecks((c) => ({ ...c, [k]: e.target.checked }))} data-testid={`aer-check-${k}`} style={{ marginTop: 3 }} />
                  <span>{label}</span>
                </label>
              ))}
            </fieldset>

            <label htmlFor="aer-lifetime" style={{ ...S.lbl, display: "block", marginTop: 12 }}>
              New approval lifetime (days)
              <select id="aer-lifetime" style={S.input} value={lifetime} onChange={(e) => setLifetime(Number(e.target.value))} data-testid="aer-lifetime-select">
                {[30, 90, 180, 365].map((d) => <option key={d} value={d}>{d} days</option>)}
              </select>
            </label>

            <label htmlFor="aer-notes" style={{ ...S.lbl, display: "block", marginTop: 12 }}>
              Supporting notes / rationale (required)
              <textarea id="aer-notes" style={{ ...S.input, minHeight: 72 }} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="aer-notes-input" />
            </label>

            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 12, fontSize: 12.5, color: C.ivoryD }}>
              <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="aer-confirm-checkbox" style={{ marginTop: 3 }} />
              <span>I confirm the above and authorize renewal of external delivery for this service.</span>
            </label>
            <label htmlFor="aer-totp" style={{ ...S.lbl, display: "block", marginTop: 10 }}>
              AdminOne 2FA code (required for HIGH-risk / regulated services)
              <input id="aer-totp" style={S.input} value={totp} inputMode="numeric" autoComplete="one-time-code" onChange={(e) => setTotp(e.target.value)} data-testid="aer-totp-input" placeholder="6-digit code" />
            </label>

            {err && <p style={S.err} data-testid="aer-modal-error">{err}</p>}
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button style={S.btn} onClick={submitReattest} disabled={busy || !allChecked || !notes} data-testid="aer-reattest-modal-submit" type="button">
                {busy ? "Working…" : "Re-attest & Renew"}
              </button>
              <button style={{ ...S.btn, ...S.btnGhost }} onClick={closeModal} data-testid="aer-modal-cancel" type="button">Close</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
